//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useRef, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useMediaQuery, useTheme } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Jitsi from 'react-jitsi';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { AFITD_JITSI_URL } from '../../system/constants';
// import { AFITD_JITSI_URL, AFITD_JITSI_JWT_TOKEN } from '../../system/constants';
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { PAGE_TITLE_EXTERNAL_VIRTUAL_INTERVIEW, PAGE_TITLE_VIRTUAL_INTERVIEW, USER_TYPE_EMP } from '../../common/constants';
// import { getJitsiConfig } from '../../common/jitsiHelper';
import { fixJitsiRoomName, getJitsiConfig, getTempJitsiConfig } from '../../common/jitsiHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../../common/elemental/AfitdPageHead';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import { logPageView } from '../../system/firebase';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import _get from 'lodash/get';
// import subMinutes from 'date-fns/subMinutes';
// import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    buttonLabel: {},
    containedDefault: {},
    containedPrimary: {},
    containedSecondary: {},
    modalDiv: {
        padding: '16px',
        border: '1px solid #1D8A8B',
        borderRadius: '4px',
    },

    typeDiv: {
        marginBottom: '50px',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        // otherProps,
        jitsiDevMode,
        isEmployer,
        jitsiToken,
        // setJitsiStarted,
        videoRef,
        theStream,
        // setTheStream,
        interviewUserDisplayName,
        // interviewUserEmail,
        // interviewUserProfilePicUrl,
        sm,
    } = params;
    const { interviewId, jobTitle, companyName, onClose } = props;
    const { ...otherClasses } = classes;

    // const jitsiRoomName = `${jobTitle} ${interviewId}`;
    // const subject = jobTitle;
    const jitsiRoomName = encodeURIComponent(fixJitsiRoomName(`${jobTitle}${interviewId}`));
    const subject = `${jobTitle} at ${companyName}`;

    const jitsiContainerStyle = { width: '90vw', minHeight: '400px', height: sm ? '530px' : '400px', maxWidth: '800px' };

    let defns = {
        // button: {
        //     color: 'primary',
        //     ...otherProps,
        //     // onClick: handleOnClick.bind(null, setOpenDialog, videoRef, setTheStream),
        //     onClick: handleOnClick.bind(null, setJitsiToken, videoRef, setTheStream, interviewUserDisplayName, interviewUserEmail, interviewUserProfilePicUrl),
        // },
        closeButton: {
            onClick: handleCloseButtonOnClick.bind(null, videoRef, theStream, onClose),
        },
        type: {
            variant: 'caption',
        },
        jitsi: jitsiDevMode
            ? {
                  containerStyle: jitsiContainerStyle,
                  config: getTempJitsiConfig({ isEmployer, subject }),
                  roomName: jitsiRoomName,
                  displayName: interviewUserDisplayName,
                  // password,
                  // loadingComponent: Loader,
                  // onAPILoad: JitsiMeetAPI => console.log('Good Morning everyone!'
              }
            : {
                  domain: AFITD_JITSI_URL,
                  jwt: jitsiToken,
                  containerStyle: jitsiContainerStyle,
                  config: getJitsiConfig({ isEmployer, subject }),
                  roomName: jitsiRoomName,
                  displayName: interviewUserDisplayName,
              },
    };

    setClassNameInDefns(defns, otherClasses);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handlePermissionGranted = (videoRef, setTheStream, stream) => {
    console.log('@@@@ ExternalVirtualInterviewDialog handlePermissionGranted');
    setTheStream(stream);
    videoRef.current.srcObject = stream;
    console.log('@@@@ ExternalVirtualInterviewDialog handlePermissionGranted...Done');
};

const handlePermissionDenied = () => {
    console.error('@@@@ ExternalVirtualInterviewDialog handlePermissionDenied');
};

// const handleOnClick = (setOpenDialog, videoRef, setTheStream) => {
//     if (setOpenDialog) {
//         setOpenDialog(true);

//         const constraints = {
//             video: true,
//         };
//         navigator.mediaDevices.getUserMedia(constraints).then(handlePermissionGranted.bind(null, videoRef, setTheStream)).catch(handlePermissionDenied);
//     }
// };

const joinMeeting = async (videoRef, setTheStream) => {
    const constraints = {
        video: true,
    };
    navigator.mediaDevices.getUserMedia(constraints).then(handlePermissionGranted.bind(null, videoRef, setTheStream)).catch(handlePermissionDenied);
};

const handleCloseButtonOnClick = (videoRef, theStream, onClose) => {
    // videoRef.current.pause();

    if (theStream) {
        const tracks = theStream.getTracks();

        tracks.forEach(function (track) {
            track.stop();
        });
    }

    onClose && onClose();
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ExternalVirtualInterviewDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const theme = useTheme();
    const appContext = useContext(AppContext);
    const { sys } = appContext;
    const videoRef = useRef();
    const sm = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { isHidden, id, title, startIcon, label, startDt, endDt, interviewStatus, onClick, autoStart, userName, ...otherProps } = props;
    const { isHidden, open, token: jitsiToken, userName, onClick, joinRequestUserType, ...otherProps } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const jitsiDevMode = sys?.jitsiDevMode;
    const isEmployer = joinRequestUserType === USER_TYPE_EMP;
    // const userInfo = isEmployer ? getUserInfoByEmployerId({ currentUser, employerId }) : currentUser;
    const interviewUserDisplayName = userName;
    // const interviewUserEmail = userInfo?.email;
    // const interviewUserProfilePicUrl = userInfo?.profilePicUrl;
    // const threeMinsBeforeStart = subMinutes(startDt, 3);
    // const currentDate = Date.now();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [msToStart, setMsToStart] = useState(differenceInMilliseconds(threeMinsBeforeStart, currentDate));
    const [msToStart, setMsToStart] = useState(0);
    // const [msToEnd, setMsToEnd] = useState(differenceInMilliseconds(endDt, currentDate));
    const [theStream, setTheStream] = useState(null);
    // const [jitsiStarted, setJitsiStarted] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_EXTERNAL_VIRTUAL_INTERVIEW);
    }, []);

    useEffect(() => {
        if (msToStart > 0) {
            const timer1 = setTimeout(() => {
                setMsToStart(-1);
            }, msToStart);
            return () => clearTimeout(timer1);
        }
        // if (msToEnd > 0) {
        //     const timer2 = setTimeout(() => {
        //         setMsToEnd(-1);
        //     }, msToEnd);
        //     return () => clearTimeout(timer2);
        // }
    });

    // useEffect(() => {
    //     if (autoStart) {
    //         joinMeeting(setJitsiToken, videoRef, setTheStream, interviewUserDisplayName, interviewUserEmail, interviewUserProfilePicUrl);
    //         // setJitsiStarted(true);
    //     }
    // }, [autoStart, interviewUserDisplayName, interviewUserEmail, interviewUserProfilePicUrl]);

    useEffect(() => {
        if (open) {
            joinMeeting(videoRef, setTheStream);
            // setJitsiStarted(true);
        }
    }, [open]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        otherProps,
        jitsiDevMode,
        isEmployer,
        jitsiToken,
        // setJitsiStarted,
        videoRef,
        theStream,
        setTheStream,
        interviewUserDisplayName,
        // interviewUserEmail,
        // interviewUserProfilePicUrl,
        sm,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    onClick && onClick(true);
    return (
        <Dialog aria-labelledby="decline-dialog" aria-describedby="decline-dialog" open={open} maxWidth="lg" fullScreen={sm}>
            <AfitdPageHead title={PAGE_TITLE_VIRTUAL_INTERVIEW} />
            <DialogTitle id="alert-dialog-title">
                <div {...defns.flex}>
                    <AfitdTypography>Live Interview in Session</AfitdTypography>
                    {sm && <CloseIcon {...defns.closeButton} />}
                </div>
            </DialogTitle>
            <DialogContent>
                <Jitsi {...defns.jitsi} />
            </DialogContent>
            <DialogActions>
                <AfitdButton {...defns.closeButton}>Close</AfitdButton>
            </DialogActions>
        </Dialog>
    );
};

export default ExternalVirtualInterviewDialog;
